import { Reducer } from 'redux'
import { UserState, UserActionTypes } from './types'
import {
  CodeDividers,
  ISplitPane,
  IWidget,
  SetCustomWidgetDimensionsPayload,
  WidgetProps,
  WidgetType
} from 'common/types/code'

export const initialState: UserState = {
  authData: undefined,
  instanceData: undefined,
  selectedCompanyId: undefined,
  fileManager: {
    selectedFilterOptions: []
  }
}

const reducer: Reducer<UserState> = (
  state: UserState = initialState,
  action: { type: UserActionTypes; payload?: any }
) => {
  switch (action.type) {
    case UserActionTypes.SET_USER_AUTH_DATA: {
      return {
        ...state,
        ...{ authData: action.payload }
      }
    }
    case UserActionTypes.SET_USER_INSTANCE_DATA: {
      return {
        ...state,
        ...{ instanceData: action.payload }
      }
    }
    case UserActionTypes.RESET_APP_USER_INFO: {
      return {
        ...state,
        ...{ instanceData: { ...state.instanceData, applicationInfo: {} } }
      }
    }
    case UserActionTypes.SET_CUSTOM_WIDGET_DIMENSIONS: {
      const { target, divider, value } = action.payload as SetCustomWidgetDimensionsPayload

      return {
        ...state,
        code: {
          ...state.code,
          customDimensions: {
            ...state.code?.customDimensions,
            [target]: {
              ...state.code?.customDimensions?.[target],
              [divider]: value
            }
          }
        }
      }
    }
    case UserActionTypes.SET_SELECTED_COMPANY_ID: {
      return {
        ...state,
        ...{ selectedCompanyId: action.payload }
      }
    }
    case UserActionTypes.SET_LICENSE_DATA: {
      return {
        ...state,
        ...{ licenseData: action.payload }
      }
    }
    case UserActionTypes.SET_INVALID_AUTH_ERROR: {
      return {
        ...state,
        ...{ authErrorMsg: action.payload }
      }
    }
    case UserActionTypes.SET_CODE_LAYOUTS: {
      return {
        ...state,
        ...{ code: { ...state.code, layouts: action.payload } }
      }
    }
    case UserActionTypes.SET_SELECTED_CODE_LAYOUT: {
      return {
        ...state,
        ...{ code: { ...state.code, selectedLayout: action.payload.codeLayoutId } }
      }
    }
    case UserActionTypes.TOGGLE_RESULT_TOOLBAR: {
      return {
        ...state,
        ...{ code: { ...state.code, showResultToolbar: !(state.code?.showResultToolbar || false) } }
      }
    }
    case UserActionTypes.SET_CODE_DIVIDER: {
      return {
        ...state,
        ...{ code: { ...state.code, ...action.payload } }
      }
    }
    case UserActionTypes.SET_WIDGET_EXPANDED: {
      return {
        ...state,
        ...{
          code: {
            ...state.code,
            widgetExpanded: action.payload
          }
        }
      }
    }
    case UserActionTypes.SET_LAST_PATH: {
      return {
        ...state,
        ...{ fileManager: { ...state.fileManager, lastPath: action.payload } }
      }
    }
    case UserActionTypes.VIEW_PREFERENCE: {
      return {
        ...state,
        ...{ fileManager: { ...state.fileManager, viewPreference: action.payload } }
      }
    }
    case UserActionTypes.SELECTED_FROM_SEARCH: {
      return {
        ...state,
        ...{ fileManager: { ...state.fileManager, selectedFromSearch: action.payload } }
      }
    }
    case UserActionTypes.PRESERVE_CONSOLE_LOG: {
      return {
        ...state,
        ...{ code: { ...state.code, preserveLog: action.payload } }
      }
    }
    case UserActionTypes.COMPANY_HAS_CHANGED: {
      return {
        ...state,
        ...{ companyHasChanged: action.payload }
      }
    }
    case UserActionTypes.SHOW_HIDDEN_FILES: {
      return {
        ...state,
        fileManager: {
          ...state.fileManager,
          showHiddenFiles: action.payload
        }
      }
    }
    case UserActionTypes.FILEMANAGER_SELECTED_FILTERS: {
      return {
        ...state,
        fileManager: {
          ...state.fileManager,
          selectedFilterOptions: action.payload
        }
      }
    }
    case UserActionTypes.NEED_TO_CHANGE_PASSWORD: {
      return {
        ...state,
        ...{ needToChangePassword: action.payload }
      }
    }
    case UserActionTypes.ENABLE_LOGIN: {
      return {
        ...state,
        ...{
          authData: {
            ...state.authData,
            otp_verified: true
          }
        }
      }
    }
    case UserActionTypes.IS_LOADING_COMPANY: {
      return {
        ...state,
        ...{ isLoadingCompany: action.payload }
      }
    }
    case UserActionTypes.RELOAD_INSTANCES: {
      return {
        ...state,
        ...{ reloadInstances: action.payload }
      }
    }
    case UserActionTypes.CHANGE_WIDGET_TYPE:
      let widgetDivider = WidgetType.PREVIEW
      let resultDivider = state.code?.resultDivider
      let previewDivider = state.code?.previewDivider
      if (action.payload === WidgetType.PREVIEW) {
        previewDivider = resultDivider
      } else {
        resultDivider = previewDivider
      }
      let widgetDividerToChange = CodeDividers.PREVIEW_DIVIDER
      if (action.payload !== widgetDivider) {
        widgetDividerToChange = CodeDividers.RESULT_DIVIDER
      }
      return {
        ...state,
        code: {
          ...state.code,
          widgetDividerToChange,
          resultDivider,
          previewDivider,
          layouts: state.code?.layouts?.map((layout, index) => ({
            ...layout,
            widgets: {
              ...layout.widgets,
              children: (layout.widgets as ISplitPane)?.children?.map((child) =>
                child.type === 'pane'
                  ? {
                      ...child,
                      children: child.children?.map((subChild: WidgetProps) => ({
                        ...subChild,
                        children: (subChild as ISplitPane).children?.map((widget) =>
                          (widget as IWidget).dividerKey === CodeDividers.RESULT_DIVIDER ||
                          (widget as IWidget).dividerKey === CodeDividers.PREVIEW_DIVIDER
                            ? {
                                ...widget,
                                contentType: action.payload,
                                dividerKey: widgetDividerToChange
                              }
                            : widget
                        )
                      }))
                    }
                  : child
              )
            }
          }))
        }
      }

    default: {
      return state
    }
  }
}

export { reducer as userReducer }
